/**
 * CheckIn Service
 * API calls for OKR weekly check-ins
 */

import { api } from './api';
import type {
  CheckIn,
  CreateCheckInRequest,
  CheckInListResponse,
  UpdateCheckInScheduleRequest,
  CheckInSchedule,
  PendingCheckInsResponse,
  CheckInHistoryResponse,
  CheckInHistoryFilters,
  CheckInInsights,
  CalendarDataResponse
} from '../types/okr';

class CheckInService {
  /**
   * Create a new check-in for an OKR
   */
  async createCheckIn(okrId: string, data: CreateCheckInRequest): Promise<CheckIn> {
    const response = await api.post(`/okrs/${okrId}/check-ins`, data);
    return response.data.data.checkIn;
  }

  /**
   * Get check-ins for an OKR with pagination
   */
  async getCheckIns(
    okrId: string,
    options?: { page?: number; limit?: number }
  ): Promise<CheckInListResponse> {
    const response = await api.get(`/okrs/${okrId}/check-ins`, { params: options });
    return response.data.data;
  }

  /**
   * Get a single check-in by ID
   */
  async getCheckInById(checkInId: string): Promise<CheckIn> {
    const response = await api.get(`/check-ins/${checkInId}`);
    return response.data.data.checkIn;
  }

  /**
   * Get check-in schedule for an OKR
   */
  async getCheckInSchedule(okrId: string): Promise<CheckInSchedule> {
    const response = await api.get(`/okrs/${okrId}/check-in-schedule`);
    return response.data.data;
  }

  /**
   * Update check-in schedule for an OKR
   */
  async updateCheckInSchedule(
    okrId: string,
    data: UpdateCheckInScheduleRequest
  ): Promise<CheckInSchedule> {
    const response = await api.patch(`/okrs/${okrId}/check-in-schedule`, data);
    return response.data.data;
  }

  /**
   * Get pending check-ins for current user
   */
  async getPendingCheckIns(): Promise<PendingCheckInsResponse> {
    const response = await api.get('/check-ins/pending');
    return response.data.data;
  }

  /**
   * Get team's pending check-ins (for managers)
   */
  async getTeamPendingCheckIns(): Promise<PendingCheckInsResponse> {
    const response = await api.get('/check-ins/team/pending');
    return response.data.data;
  }

  /**
   * Get check-in history across all user's OKRs
   */
  async getCheckInHistory(filters?: CheckInHistoryFilters): Promise<CheckInHistoryResponse> {
    const response = await api.get('/check-ins/history', { params: filters });
    return response.data.data;
  }

  /**
   * Get check-in insights for manager's team
   */
  async getCheckInInsights(filters?: { cycleId?: string; startDate?: string; endDate?: string }): Promise<CheckInInsights> {
    const response = await api.get('/check-ins/insights', { params: filters });
    return response.data.data;
  }

  /**
   * Get calendar data for check-ins
   */
  async getCalendarData(startDate: string, endDate: string): Promise<CalendarDataResponse> {
    const response = await api.get('/check-ins/calendar', { params: { startDate, endDate } });
    return response.data.data;
  }

  /**
   * Get org-wide pending check-ins (for account admins)
   */
  async getOrgPendingCheckIns(): Promise<PendingCheckInsResponse> {
    const response = await api.get('/check-ins/org/pending');
    return response.data.data;
  }

  /**
   * Get org-wide check-in history (for account admins)
   */
  async getOrgCheckInHistory(filters?: CheckInHistoryFilters): Promise<CheckInHistoryResponse> {
    const response = await api.get('/check-ins/org/history', { params: filters });
    return response.data.data;
  }

  /**
   * Get org-wide check-in insights (for account admins)
   */
  async getOrgCheckInInsights(filters?: { cycleId?: string; startDate?: string; endDate?: string }): Promise<CheckInInsights> {
    const response = await api.get('/check-ins/org/insights', { params: filters });
    return response.data.data;
  }
}

export const checkInService = new CheckInService();
